import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAfiliados } from '../../reducers/afiliado_slice';
import { fetchCuotas } from '../../reducers/cuota_slice';
import { fetchNumeroCertificado } from '../../reducers/valoradoVen_slice';
import { Table, Form, InputGroup } from 'react-bootstrap';
import Swal from 'sweetalert2';
import AfiliadoCert1 from './AfiliadoCert1';
import AfiliadoCert2 from './AfiliadoCert2';
import AfiliadoCert3 from './AfiliadoCert3';
import AfiliadoEstadoCuentaModal from './AfiliadoEstadoCuentaModal';
import { useNavigate } from 'react-router-dom';
import { fetchDatGeneral } from '../../reducers/datGeneral_slice';
import { apellidosNombreFullPersona } from '../../util/customFormatter';

// ✅ Sanciones
import { fetchSanciones } from '../../reducers/sancion_slice';

const AfiliadosCert = () => {
    const dispatch = useDispatch();
    const afiliados = useSelector(state => state.afiliado.afiliados || []);
    const cuotas = useSelector(state => state.cuota.cuotas || []);
    const statusGetCuotas = useSelector(state => state.cuota.statusGet);
    const datgeneralList = useSelector((state) => state.datgeneral.datgeneralList);

    // ✅ Sanciones (traídas 1 vez)
    const sanciones = useSelector((state) => state.sancion.sanciones || []);

    const [searchTerm, setSearchTerm] = useState('');
    const [showCertModal, setShowCertModal] = useState(false);
    const [showEstadoModal, setShowEstadoModal] = useState(false);
    const [certType, setCertType] = useState(null);
    const [selectedAfiliado, setSelectedAfiliado] = useState(null);
    const [certificadoData, setCertificadoData] = useState(null); // (se usa solo para cert 1 y 2)
    const xcuotas = datgeneralList[0]?.cuotas_tolerancia || 0;
    const navigate = useNavigate();

    // ---------- Utilidades sanción vigente (parse local) ----------
    const hoy0 = () => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const parseLocalYMD = (ymd) => {
        if (!ymd) return null;
        const [y, m, d] = String(ymd).split('-').map(Number);
        return new Date(y, (m || 1) - 1, d || 1);
    };

    const esVigenteHoy = (r) => {
        if (!r?.fecha_inicio) return false;

        const h = hoy0().getTime();
        const ini = parseLocalYMD(r.fecha_inicio);
        if (!ini) return false;
        ini.setHours(0, 0, 0, 0);
        const iniT = ini.getTime();

        let finT = null;
        if (r.fecha_fin) {
            const fin = parseLocalYMD(r.fecha_fin);
            if (fin) {
                fin.setHours(23, 59, 59, 999);
                finT = fin.getTime();
            }
        }

        if (h < iniT) return false;
        if (finT !== null && h > finT) return false;
        return true;
    };

    const getSancionVigenteAfiliado = (idAfiliado) => {
        const rows = (sanciones || []).filter((s) => (s?.id_afiliado ?? s?.afiliado?.id) === idAfiliado);
        const vigentes = rows.filter(esVigenteHoy);
        if (!vigentes.length) return null;

        vigentes.sort((a, b) => {
            const aa = parseLocalYMD(a.fecha_inicio)?.getTime() || new Date(a.createdAt).getTime();
            const bb = parseLocalYMD(b.fecha_inicio)?.getTime() || new Date(b.createdAt).getTime();
            return bb - aa;
        });

        return vigentes[0];
    };

    useEffect(() => {
        dispatch(fetchDatGeneral());
        dispatch(fetchAfiliados());
        dispatch(fetchCuotas());
        dispatch(fetchSanciones({ limit: 10000, offset: 0, order: "createdAt:DESC" }));
    }, [dispatch]);

    const handleCertificadoClick = async (tipo, afiliado) => {
        const cuotasAdeudadas = getCuotasAdeudadas(afiliado.id);

        if (cuotasAdeudadas > xcuotas) {
            Swal.fire({
                title: 'Afiliado con deudas',
                text: `El afiliado ${afiliado.persona.nombre} tiene ${cuotasAdeudadas} cuotas adeudadas. Debe ponerse al día antes de generar el certificado.`,
                icon: 'warning',
                confirmButtonText: 'Aceptar',
            });
            return;
        }

        // ✅ CERTIFICADO 3: NO requiere comprar ni usar certificadoData
        if (tipo === 3) {
            const vigente = getSancionVigenteAfiliado(afiliado.id);

            if (vigente) {
                Swal.fire({
                    title: "Afiliado con sanción vigente",
                    html: `
                        <div style="text-align:left">
                            <div><b>Afiliado:</b> ${afiliado?.persona?.apellido1 || ""} ${afiliado?.persona?.apellido2 || ""}, ${afiliado?.persona?.nombre || ""}</div>
                            <div><b>Reg.:</b> ${afiliado?.num_reg || "—"} — <b>CI:</b> ${afiliado?.persona?.ci || "—"}</div>
                            <hr/>
                            <div><b>Tipo sanción:</b> ${vigente?.tipo_sancion || "—"}</div>
                            <div><b>Inicio:</b> ${vigente?.fecha_inicio ? parseLocalYMD(vigente.fecha_inicio).toLocaleDateString() : "—"}</div>
                            <div><b>Fin:</b> ${vigente?.fecha_fin ? parseLocalYMD(vigente.fecha_fin).toLocaleDateString() : "—"}</div>
                            <div class="mt-2" style="color:#666">No es posible emitir el Certificado de No Sanción mientras exista una sanción vigente.</div>
                        </div>
                    `,
                    icon: "warning",
                    confirmButtonText: "Aceptar",
                });
                return;
            }

            // ✅ Abrir modal Cert 3 directo (sin fetchNumeroCertificado)
            setSelectedAfiliado(afiliado);
            setCertType(3);
            setShowCertModal(true);
            return;
        }

        // ---- Certificados 1 y 2: flujo actual con compra ----
        try {
            const nombreCertificado = `CERTIFICADO${tipo}`;
            const response = await dispatch(fetchNumeroCertificado({
                id_afiliado: afiliado.id,
                nombre_certificado: nombreCertificado,
            })).unwrap();

            if (!response.numeroCertificado) {
                Swal.fire({
                    title: 'Sin certificados disponibles',
                    text: `Debe comprar más certificados tipo ${nombreCertificado} antes de poder generarlo.`,
                    icon: 'warning',
                    confirmButtonText: 'Aceptar',
                });
            } else {
                setCertificadoData(response);
                setSelectedAfiliado(afiliado);
                setCertType(tipo);
                setShowCertModal(true);
            }
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: error.error || 'No se pudo obtener el certificado.',
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
        }
    };

    const handleVisualizarEstado = (afiliado) => {
        setSelectedAfiliado(afiliado);
        setShowEstadoModal(true);
    };

    const handleBack = () => {
        navigate('/admin');
    };

    const getCuotasAdeudadas = (afiliadoId) => {
        const hoy = new Date();
        const diaActual = hoy.getDate();
        const mesActual = hoy.getMonth();
        const anioActual = hoy.getFullYear();

        const ultimoMesEvaluado = diaActual <= 15 ? mesActual - 1 : mesActual;

        const cuotasFiltradas = cuotas.filter(cuota => {
            const cuotaFecha = new Date(cuota.cuota + "-01");
            return (
                cuota.id_afiliado === afiliadoId &&
                cuota.estado === "no_pagada" &&
                (cuotaFecha.getFullYear() < anioActual ||
                    (cuotaFecha.getFullYear() === anioActual && cuotaFecha.getMonth() < ultimoMesEvaluado))
            );
        });

        return cuotasFiltradas.length;
    };

    const filteredAfiliados = afiliados.filter(afiliado =>
        `${apellidosNombreFullPersona(afiliado.persona, "ci")} ${afiliado.num_reg}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
    );

    return (
        <>
            <div className="list-container list6">
                <div className="list-container-header">
                    <h2 className="header-title">Gestión de Certificados AFILIADOS</h2>
                    <InputGroup size="sm" className="mt-2">
                        <InputGroup.Text>
                            <i className="bi bi-search"></i>
                        </InputGroup.Text>
                        <Form.Control
                            size="sm"
                            type="text"
                            placeholder="Buscar por número registro, C.I. o nombre del afiliado"
                            className="form-control"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </InputGroup>
                    <div className="left-buttons">
                        <button title="Volver" onClick={handleBack} className="icon-button btn-transparent">
                            <i className="bi bi-arrow-left"></i>
                        </button>
                    </div>
                </div>

                {statusGetCuotas === 'loading' ? (
                    <p>Cargando cuotas...</p>
                ) : (
                    <div className="table-container list6">
                        <Table bordered hover size="sm">
                            <thead className="text-center align-middle">
                                <tr>
                                    <th style={{ width: '5%' }}>#</th>
                                    <th style={{ width: '10%' }}>Nro. Reg.</th>
                                    <th style={{ width: '20%' }}>1er Apellido</th>
                                    <th style={{ width: '20%' }}>2do Apellido</th>
                                    <th style={{ width: '25%' }}>Nombre</th>
                                    <th>CI</th>
                                    <th style={{ width: '17%' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAfiliados.map((afiliado, index) => (
                                    <tr key={afiliado.id}>
                                        <td>{index + 1}</td>
                                        <td>{afiliado.num_reg}</td>
                                        <td>{afiliado.persona.apellido1 || ""}</td>
                                        <td>{afiliado.persona.apellido2 || ""}</td>
                                        <td>{afiliado.persona.nombre}</td>
                                        <td>{afiliado.persona.ci}</td>
                                        <td className="text-center">
                                            <button
                                                className="icon-button btn-secondary-outline me-2"
                                                title="Generar Certtificado de Registro y No Adeudo"
                                                onClick={() => handleCertificadoClick(1, afiliado)}
                                            >
                                                <i className="bi bi-file-earmark-text"></i>
                                            </button>
                                            <button
                                                className="icon-button btn-secondary-outline me-2"
                                                title="Generar Certificado de Licitación"
                                                onClick={() => handleCertificadoClick(2, afiliado)}
                                            >
                                                <i className="bi bi-file-earmark-text-fill"></i>
                                            </button>
                                            <button
                                                className="icon-button btn-secondary-outline me-2"
                                                title="Generar Certificado 3 (No Sanción)"
                                                onClick={() => handleCertificadoClick(3, afiliado)}
                                            >
                                                <i className="bi bi-file-earmark-richtext"></i>
                                            </button>
                                            <button
                                                className="icon-button btn-secondary-outline"
                                                title="Visualizar Estado"
                                                onClick={() => handleVisualizarEstado(afiliado)}
                                            >
                                                <i className="bi bi-eye"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </div>

            {showCertModal && certType === 1 && (
                <AfiliadoCert1
                    show={showCertModal}
                    handleClose={() => setShowCertModal(false)}
                    afiliado={selectedAfiliado}
                    certificadoData={certificadoData}
                />
            )}

            {showCertModal && certType === 2 && (
                <AfiliadoCert2
                    show={showCertModal}
                    handleClose={() => setShowCertModal(false)}
                    afiliado={selectedAfiliado}
                    certificadoData={certificadoData}
                />
            )}

            {showCertModal && certType === 3 && (
                <AfiliadoCert3
                    show={showCertModal}
                    handleClose={() => setShowCertModal(false)}
                    afiliado={selectedAfiliado}
                />
            )}

            {showEstadoModal && (
                <AfiliadoEstadoCuentaModal
                    show={showEstadoModal}
                    handleClose={() => setShowEstadoModal(false)}
                    id_afiliado={selectedAfiliado.id}
                />
            )}
        </>
    );
};

export default AfiliadosCert;